/**
 * Карта «Экибастуз» для «Полёта птицы» — открытый мир на острове ~1,6×1,6 км.
 * Зоны: долина ЦОД (центр), ГРЭС-2 с трубой (север), разрез «Богатырь»
 * (восток), ЛЭП 1150 кВ, город с панельками, ДК и стелой (юго-запад).
 * Всё из примитивов, мир детерминирован по seed — у всех игроков одинаковый.
 * Время суток меняется: 0 — день, 0,5 — закат, 1 — ночь с огнями.
 *
 * Перенесено из прототипа groove-bird-prototype/ekibastuz/map.html.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export interface BirdFlightWorld {
  /** Высота земли в точке — птица не опускается ниже. */
  height(x: number, z: number): number;
  /** Звёзды для сбора, в координатах мира. */
  readonly pickups: THREE.Object3D[];
  readonly spawn: { x: number; y: number; z: number; yaw: number };
  /** Радиус острова: дальше птицу разворачивает к центру. */
  readonly radius: number;
  readonly maxY: number;
  update(dt: number, elapsed: number, timeOfDay: number, cameraPosition: THREE.Vector3): void;
}

export function* buildEkibastuzWorld(scene: THREE.Scene): Generator<number, BirdFlightWorld, void> {
  // ─── Случайность по seed: одинаковый мир у всех игроков ───────────────────
  function mulberry32(a) {
    return function () {
      a |= 0; a = a + 0x6D2B79F5 | 0;
      let t = Math.imul(a ^ a >>> 15, 1 | a);
      t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
      return ((t ^ t >>> 14) >>> 0) / 4294967296;
    };
  }
  const rnd = mulberry32(20260929);
  const rand = (a, b) => a + rnd() * (b - a);
  const pick = (list) => list[Math.floor(rnd() * list.length)];
  const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

  yield 0.07;
  // ─── Размер мира ─────────────────────────────────────────────────────────
  // Остров сжат до 0.6 от первой версии (1300 → 780): на телефоне весь мир
  // рисовался каждый кадр, а пролететь его по прямой занимало две минуты.
  // Сжата раскладка — расстояния между зонами, — а не сами здания: трубы,
  // корпуса и дома прежнего размера.
  const ISLAND = 780;
  // Небо идёт за камерой и должно помещаться до её дальней плоскости
  // (camera.far в birdFlightGame — 1600).
  const SKY_RADIUS = 1400;
  // Туман съедает дальний план: дальше FOG_FAR объекты не видны, и
  // секторы за ним отсекаются вместе с камерой.
  const FOG_NEAR = 250;
  const FOG_FAR = 1100;

  yield 0.13;
  // ─── Небо: шейдерная сфера, цвета меняются со временем суток ─────────────
  const skyUniforms = {
    top: { value: new THREE.Color() },
    mid: { value: new THREE.Color() },
    horizon: { value: new THREE.Color() },
    sunDir: { value: new THREE.Vector3() },
    sunColor: { value: new THREE.Color() },
    sunStrength: { value: 1 },
  };
  const sky = new THREE.Mesh(new THREE.SphereGeometry(SKY_RADIUS, 32, 16), new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: skyUniforms,
    vertexShader: 'varying vec3 vDir; void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }',
    fragmentShader: `
      uniform vec3 top, mid, horizon, sunColor, sunDir; uniform float sunStrength; varying vec3 vDir;
      void main(){
        float h = vDir.y;
        vec3 c = h > 0.18 ? mix(mid, top, smoothstep(0.18, 0.8, h)) : mix(horizon, mid, smoothstep(-0.02, 0.18, h));
        float d = max(dot(normalize(vDir), normalize(sunDir)), 0.0);
        c += sunColor * (pow(d, 2500.0) * 4.0 + pow(d, 90.0) * 0.35) * sunStrength;
        gl_FragColor = vec4(c, 1.0);
        #include <colorspace_fragment>
      }`,
  }));
  scene.add(sky);

  // Звёзды на ночном небе.
  const nightStars = (() => {
    const pts = [];
    for (let i = 0; i < 1400; i++) {
      const u = rnd() * Math.PI * 2; const
        v = Math.acos(rand(0.05, 1));
      const r = SKY_RADIUS * 0.95;
      pts.push(Math.sin(v) * Math.cos(u) * r, Math.cos(v) * r, Math.sin(v) * Math.sin(u) * r);
    }
    const g = new THREE.BufferGeometry(); g.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    const p = new THREE.Points(g, new THREE.PointsMaterial({
      color: 0xffffff, size: 2.2, sizeAttenuation: false, transparent: true, fog: false,
    }));
    scene.add(p); return p;
  })();

  const fog = new THREE.Fog(0xffffff, FOG_NEAR, FOG_FAR);
  scene.fog = fog;
  const hemi = new THREE.HemisphereLight(0xffffff, 0x3a3048, 1);
  scene.add(hemi);
  const sun = new THREE.DirectionalLight(0xffffff, 1);
  scene.add(sun);

  const lambert = (color, extra = {}) => new THREE.MeshLambertMaterial({ color, flatShading: true, ...extra });
  const box = new THREE.BoxGeometry(1, 1, 1);

  // Огни: днём тусклые, ночью горят. Каждый — материал + дневной и ночной цвет.
  const lights = [];
  function lightMat(dayColor, nightColor) {
    const m = new THREE.MeshBasicMaterial({ color: dayColor });
    lights.push({ m, day: new THREE.Color(dayColor), night: new THREE.Color(nightColor) });
    return m;
  }

  // Секторы: инстансы и склеенные меши режутся по сетке SECTOR × SECTOR м, у
  // каждого куска своя ограничивающая сфера — three.js отбрасывает куски вне
  // камеры. Целым мир отсечь нельзя: инстансы по всему острову — одна сфера
  // на весь остров, всегда видимая.
  const SECTOR = 400;
  const sectorOf = (x, z) => `${Math.floor(x / SECTOR)}:${Math.floor(z / SECTOR)}`;

  // Инстансинг: сотни одинаковых объектов — один draw call на сектор.
  function instanced(geo, material, list) {
    const bySector = new Map();
    list.forEach((it) => {
      const key = sectorOf(it.x, it.z);
      if (!bySector.has(key)) bySector.set(key, []);
      bySector.get(key).push(it);
    });
    const m = new THREE.Matrix4(); const q = new THREE.Quaternion(); const
      e = new THREE.Euler();
    bySector.forEach((items) => {
      const mesh = new THREE.InstancedMesh(geo, material, items.length);
      items.forEach((it, i) => {
        if (it.q) q.copy(it.q); else { e.set(it.rx || 0, it.ry || 0, it.rz || 0); q.setFromEuler(e); }
        m.compose(new THREE.Vector3(it.x, it.y, it.z), q, new THREE.Vector3(it.sx || 1, it.sy || 1, it.sz || 1));
        mesh.setMatrixAt(i, m);
      });
      mesh.computeBoundingSphere();
      scene.add(mesh);
    });
  }
  function add(geo, material, x, y, z, sx = 1, sy = 1, sz = 1, ry = 0) {
    const m = new THREE.Mesh(geo, material);
    m.position.set(x, y, z); m.scale.set(sx, sy, sz); m.rotation.y = ry; scene.add(m); return m;
  }

  yield 0.20;
  // ─── Зоны острова ────────────────────────────────────────────────────────
  const ZONES = {
    dc: { x: 0, z: 0 }, // долина ЦОД
    gres: { x: -72, z: -432 }, // ГРЭС-2
    pit: {
      x: 432, z: 48, rx: 180, rz: 130,
    }, // разрез «Богатырь»
    city: { x: -380, z: 300 }, // город — после референсов
  };

  // Рельеф: ровная степь, холмы у края острова, карьер уступами.
  const PIT_STEP = 11; const
    PIT_LEVELS = 7;
  function pitLevel(x, z) {
    const p = ZONES.pit;
    const r = Math.hypot((x - p.x) / p.rx, (z - p.z) / p.rz);
    return r >= 1 ? -1 : Math.min(PIT_LEVELS - 1, Math.floor((1 - r) * PIT_LEVELS * 1.15));
  }
  // Высота полки уступа в точке карьера.
  function pitFloor(x, z) {
    const p = ZONES.pit; const
      r = Math.hypot((x - p.x) / p.rx, (z - p.z) / p.rz);
    const k = Math.min(PIT_LEVELS - 1, Math.floor((1 - r) * PIT_LEVELS * 1.15));
    return -(k + 1) * PIT_STEP;
  }
  function height(x, z) {
    const level = pitLevel(x, z);
    if (level >= 0) return -PIT_LEVELS * PIT_STEP - 1; // дно; стенки рисует уступчатая чаша ниже
    const r = Math.hypot(x, z);
    // Город стоит на ровном: холмы гасим рядом с ним.
    const nearCity = smooth(260, 420, Math.hypot(x - ZONES.city.x, z - ZONES.city.z));
    const hill = smooth(ISLAND * 0.6, ISLAND * 1.05, r);
    return ((Math.sin(x * 0.009) * Math.cos(z * 0.011) * 0.5 + 0.5) * 70 * hill + Math.sin(x * 0.05 + z * 0.03) * 1.2) * nearCity;
  }

  {
    const seg = 100; // ячейка ~19 м — как была при 160 на старом острове
    // Высоты — по узлам сетки, потом разворот в треугольники: в 6 раз
    // меньше вызовов height(), чем по развёрнутой сетке (как в Алматы).
    const grid = new THREE.PlaneGeometry(ISLAND * 2.4, ISLAND * 2.4, seg, seg);
    grid.rotateX(-Math.PI / 2);
    const gridPos = grid.attributes.position;
    for (let i = 0; i < gridPos.count; i++) gridPos.setY(i, height(gridPos.getX(i), gridPos.getZ(i)));
    const geo = grid.toNonIndexed();
    grid.dispose();
    const pos = geo.attributes.position;
    const steppe = [0x9a7c52, 0x9f8156, 0x94774e, 0xa3865a, 0x8c7c4e, 0x98804f].map((c) => new THREE.Color(c));
    const coal = [0x2b2622, 0x3a322a, 0x4a3f33, 0x5a4b3b].map((c) => new THREE.Color(c));
    const colors = [];
    for (let f = 0; f < pos.count / 3; f++) {
      const cx = (pos.getX(f * 3) + pos.getX(f * 3 + 1) + pos.getX(f * 3 + 2)) / 3;
      const cz = (pos.getZ(f * 3) + pos.getZ(f * 3 + 1) + pos.getZ(f * 3 + 2)) / 3;
      const lvl = pitLevel(cx, cz);
      const c = lvl >= 0 ? coal[(lvl + (rnd() < 0.3 ? 1 : 0)) % coal.length] : pick(steppe);
      for (let k = 0; k < 3; k++) colors.push(c.r, c.g, c.b);
    }
    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    scene.add(new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  }

  yield 0.27;
  // ─── Степь не пустая: колки берёз, кусты, грунтовки ──────────────────────
  {
    const trunks = []; const crowns = []; const
      bushes = [];
    const clear = (x, z) => Math.hypot(x - ZONES.dc.x, z - ZONES.dc.z) > 250 && pitLevel(x, z) < 0
      && Math.hypot(x - ZONES.pit.x, z - ZONES.pit.z) > 230 && Math.hypot(x - ZONES.gres.x, z - ZONES.gres.z) > 200
      && Math.hypot(x - ZONES.city.x, z - ZONES.city.z) > 260;
    // Плотность — как на прежнем острове: площадь стала 0.36 от прежней.
    for (let g = 0; g < 22; g++) {
      const gx = rand(-ISLAND, ISLAND); const
        gz = rand(-ISLAND, ISLAND);
      if (!clear(gx, gz) || Math.hypot(gx, gz) > ISLAND * 0.95) continue;
      const n = Math.floor(rand(6, 18));
      for (let i = 0; i < n; i++) {
        const x = gx + rand(-30, 30); const z = gz + rand(-30, 30); const y = height(x, z); const
          h = rand(7, 12);
        trunks.push({
          x, y: y + h * 0.35, z, sx: 0.5, sy: h * 0.7, sz: 0.5,
        });
        crowns.push({
          x, y: y + h * 0.8, z, sx: rand(2.5, 3.8), sy: h * 0.45, sz: rand(2.5, 3.8), ry: rnd() * 3,
        });
      }
    }
    for (let i = 0; i < 330; i++) {
      const x = rand(-ISLAND, ISLAND); const
        z = rand(-ISLAND, ISLAND);
      if (!clear(x, z)) continue;
      bushes.push({
        x, y: height(x, z) + 0.6, z, sx: rand(1.2, 2.6), sy: rand(0.8, 1.4), sz: rand(1.2, 2.6), ry: rnd() * 3,
      });
    }
    instanced(box, lambert(0xece6da), trunks);
    instanced(new THREE.IcosahedronGeometry(1, 0), lambert(0x9aa65a), crowns);
    instanced(new THREE.IcosahedronGeometry(1, 0), lambert(0x7d7a45), bushes);
  }

  yield 0.33;
  // ─── Дороги и железная дорога ────────────────────────────────────────────
  const lampPoles = []; const
    lampHeads = [];
  const roadMat = lambert(0x3b3d42);
  const lineMat = lightMat(0xcfc6b0, 0xf2e6c8);
  function road(x1, z1, x2, z2, w, lamps = 0) {
    const dx = x2 - x1; const dz = z2 - z1; const len = Math.hypot(dx, dz); const
      ry = Math.atan2(dx, dz);
    add(box, roadMat, (x1 + x2) / 2, 0.35, (z1 + z2) / 2, w, 0.3, len, ry);
    add(box, lineMat, (x1 + x2) / 2, 0.55, (z1 + z2) / 2, 0.3, 0.05, len, ry);
    if (lamps) {
      for (let s = 0; s <= len; s += lamps) {
        const x = x1 + dx * s / len + Math.cos(ry) * (w / 2 + 1); const
          z = z1 + dz * s / len - Math.sin(ry) * (w / 2 + 1);
        lampPoles.push({
          x, y: 4, z, sx: 0.25, sy: 8, sz: 0.25,
        });
        lampHeads.push({
          x, y: 8.2, z, sx: 0.9, sy: 0.5, sz: 0.9,
        });
      }
    }
  }
  road(0, -300, 0, 300, 12, 26); // сквозь кампус
  road(0, -300, ZONES.gres.x + 60, ZONES.gres.z + 90, 12, 40); // к ГРЭС
  road(0, 300, ZONES.city.x + 120, ZONES.city.z - 60, 14, 40); // к городу
  road(195, 0, ZONES.pit.x - ZONES.pit.rx - 10, ZONES.pit.z, 12, 0); // к разрезу

  // Железная дорога: от разреза к ГРЭС, по ней угольные составы.
  const railPath = [
    new THREE.Vector3(ZONES.pit.x - ZONES.pit.rx * 0.4, 0, ZONES.pit.z - ZONES.pit.rz - 30),
    new THREE.Vector3(252, 0, -198),
    new THREE.Vector3(72, 0, -336),
    new THREE.Vector3(ZONES.gres.x + 140, 0, ZONES.gres.z + 40),
  ];
  {
    const sleepers = [];
    for (let i = 0; i < railPath.length - 1; i++) {
      const a = railPath[i]; const b = railPath[i + 1]; const len = a.distanceTo(b); const
        ry = Math.atan2(b.x - a.x, b.z - a.z);
      add(box, lambert(0x6e6358), (a.x + b.x) / 2, 0.4, (a.z + b.z) / 2, 5, 0.5, len, ry);
      for (let s = 0; s < len; s += 2.5) {
        const p = a.clone().lerp(b, s / len);
        sleepers.push({
          x: p.x, y: 0.75, z: p.z, sx: 3.6, sy: 0.2, sz: 0.6, ry,
        });
      }
      [-0.8, 0.8].forEach((o) => add(box, lambert(0x9a9ca0), (a.x + b.x) / 2 + Math.cos(ry) * o, 0.95, (a.z + b.z) / 2 - Math.sin(ry) * o, 0.15, 0.2, len, ry));
    }
    instanced(box, lambert(0x4a3b2e), sleepers);
    // Состав: тепловоз + полувагоны с горкой угля.
    const a = railPath[1]; const b = railPath[2]; const ry = Math.atan2(b.x - a.x, b.z - a.z); const
      dir = b.clone().sub(a).normalize();
    // Состав помещается на отрезке пути: 14 × 13.5 м.
    for (let i = 0; i < 14; i++) {
      const p = a.clone().addScaledVector(dir, 25 + i * 13.5);
      if (i === 0) {
        add(box, lambert(0x2f6b4f), p.x, 3.2, p.z, 3.2, 4.4, 12, ry);
        add(box, lightMat(0x6d6a5a, 0xffe7a0), p.x - dir.x * 6.1, 3.8, p.z - dir.z * 6.1, 1.6, 0.8, 0.1, ry);
      } else {
        add(box, lambert(pick([0x7a3b2a, 0x6b4a36, 0x5c5f63])), p.x, 2.4, p.z, 3.2, 2.6, 12.5, ry);
        add(new THREE.IcosahedronGeometry(1, 0), lambert(0x1c1a19), p.x, 3.9, p.z, 1.4, 0.8, 5.5, ry);
      }
    }
  }

  yield 0.40;
  // ─── Долина ЦОД: компактный кампус ───────────────────────────────────────
  const halls = [];
  {
    const { x: ox, z: oz } = ZONES.dc;
    const wallDark = lambert(0x2c3038); const wallLight = lambert(0xc9ccd2); const
      roofMat = lambert(0xdfe2e6);
    const stripe = lightMat(0x3d6f9a, 0x4fb3ff);
    const windowMat = lightMat(0x263a52, 0x2f8fff);
    const windows = []; const chillers = []; const fans = []; const
      fence = [];
    add(box, lambert(0x55575c), ox, 0.2, oz, 380, 0.4, 250);

    function hall(cx, cz, w, d, h) {
      add(box, wallDark, cx, h * 0.36, cz, w, h * 0.72, d);
      add(box, wallLight, cx, h * 0.86, cz, w, h * 0.28, d);
      add(box, roofMat, cx, h + 0.3, cz, w + 0.6, 0.6, d + 0.6);
      add(box, stripe, cx, h * 0.72, cz, w + 0.2, 0.45, d + 0.2);
      for (let x = -w / 2 + 2.5; x < w / 2 - 2; x += 2) {
        for (let y = 1.3; y < h * 0.66; y += 2.1) {
          if (rnd() < 0.18) continue;
          [d / 2 + 0.06, -d / 2 - 0.06].forEach((z) => windows.push({
            x: cx + x, y, z: cz + z, sx: 1.3, sy: 1.4, sz: 0.1,
          }));
        }
      }
      for (let x = -w / 2 + 3.5; x < w / 2 - 3; x += 4.5) {
        for (let z = -d / 2 + 3.5; z < d / 2 - 3; z += 5) {
          chillers.push({
            x: cx + x, y: h + 1.5, z: cz + z, sx: 3.2, sy: 2, sz: 3.4,
          });
          fans.push({
            x: cx + x, y: h + 2.55, z: cz + z, sx: 1.1, sy: 0.12, sz: 1.1,
          });
        }
      }
      halls.push({
        x: cx, z: cz, w, d, h,
      });
    }
    [-62, 62].forEach((z) => [-120, 0, 120].forEach((x) => hall(ox + x + rand(-3, 3), oz + z, rand(72, 84), rand(40, 46), rand(13, 16))));
    road(ox - 190, oz, ox + 190, oz, 10, 20);
    for (let x = -190; x <= 190; x += 3.5) {
      [-125, 125].forEach((z) => fence.push({
        x: ox + x, y: 1.1, z: oz + z, sx: 0.12, sy: 2.2, sz: 0.12,
      }));
    }
    for (let z = -125; z <= 125; z += 3.5) {
      [-190, 190].forEach((x) => fence.push({
        x: ox + x, y: 1.1, z: oz + z, sx: 0.12, sy: 2.2, sz: 0.12,
      }));
    }
    instanced(box, windowMat, windows);
    instanced(box, lambert(0xb9bec6), chillers);
    instanced(new THREE.CylinderGeometry(0.5, 0.5, 1, 10), lambert(0x3c4048), fans);
    instanced(box, lambert(0x8a9098), fence);

    // Стела у въезда.
    const c = document.createElement('canvas'); c.width = 256; c.height = 640;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#0f1520'; ctx.fillRect(0, 0, 256, 640);
    ctx.fillStyle = '#5fd0ff'; ctx.font = 'bold 34px -apple-system, sans-serif';
    ['ДОЛИНА', 'ЦЕНТРОВ', 'ОБРАБОТКИ', 'ДАННЫХ'].forEach((line, i) => ctx.fillText(line, 22, 90 + i * 46));
    ctx.fillStyle = '#ffffff'; ctx.font = '24px -apple-system, sans-serif';
    ctx.fillText('ЭКИБАСТУЗ', 22, 470); ctx.fillStyle = '#9fb3c8'; ctx.fillText('КАЗАХСТАН', 22, 502);
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const face = new THREE.MeshBasicMaterial({ map: tex }); const
      dark = lambert(0x1a1f28);
    const stela = new THREE.Mesh(box, [dark, dark, dark, dark, face, dark]);
    stela.scale.set(10, 26, 2.4); stela.position.set(ox - 30, 13, oz + 150); scene.add(stela);
  }

  yield 0.47;
  // ─── ГРЭС-2: главный корпус, труба 420 м (самая высокая в мире), вторая труба ─
  const redLights = [];
  const smokeSources = [];
  {
    const { x: gx, z: gz } = ZONES.gres;
    add(box, lambert(0xcfc8bc), gx, 30, gz, 170, 60, 55); // главный корпус
    add(box, lambert(0x9c948a), gx - 20, 45, gz - 40, 120, 90, 30); // котельное отделение
    add(box, lambert(0x6f7680), gx + 110, 12, gz + 10, 40, 24, 70); // ОРУ/трансформаторы
    // Окна корпуса — вечером горят тёплым.
    const winMat = lightMat(0x6e6a62, 0xffcf7a); const
      wins = [];
    for (let x = -80; x < 80; x += 4) {
      for (let y = 6; y < 56; y += 7) {
        if (rnd() > 0.35) {
          wins.push({
            x: gx + x, y, z: gz + 27.6, sx: 2.2, sy: 3, sz: 0.2,
          });
        }
      }
    }
    instanced(box, winMat, wins);

    function chimney(x, z, h, r) {
      const bands = 12;
      for (let s = 0; s < bands; s++) {
        const t0 = s / bands; const
          t1 = (s + 1) / bands;
        const seg = new THREE.Mesh(
          new THREE.CylinderGeometry(r * (1 - t1 * 0.55), r * (1 - t0 * 0.55), h / bands, 16),
          lambert(s >= bands - 3 ? (s % 2 ? 0xf2f2f2 : 0xc8372d) : 0xd8d3cc),
        );
        seg.position.set(x, h * (t0 + t1) / 2, z); scene.add(seg);
      }
      // Красные сигнальные огни по высоте — ночью видно издалека.
      [0.35, 0.65, 1].forEach((t) => {
        const l = new THREE.Mesh(new THREE.SphereGeometry(r * 0.28, 8, 6), lightMat(0x8a2a22, 0xff3b30));
        l.position.set(x + r * (1 - t * 0.55), h * t, z); scene.add(l); redLights.push(l);
      });
      smokeSources.push({
        x, y: h + 8, z, r,
      });
    }
    chimney(gx + 30, gz - 80, 300, 16); // 420 м в жизни, в игре 300 — всё равно выше всего на острове
    chimney(gx - 70, gz - 80, 170, 11);

    // Водохранилище-охладитель рядом со станцией.
    const water = new THREE.Mesh(new THREE.CircleGeometry(1, 40), lambert(0x4f7f9a, { flatShading: false }));
    water.rotation.x = -Math.PI / 2; water.scale.set(150, 100, 1); water.position.set(gx - 260, 0.6, gz + 40); scene.add(water);
  }
  // Дым — поток мягких клубов: рождаются у среза трубы, поднимаются, их
  // сносит ветром, растут и тают; дотаявший клуб возвращается к трубе.
  const smokeTex = (() => {
    const c = document.createElement('canvas'); c.width = c.height = 128;
    const g = c.getContext('2d'); const
      grd = g.createRadialGradient(64, 64, 4, 64, 64, 62);
    grd.addColorStop(0, 'rgba(255,255,255,0.95)'); grd.addColorStop(0.45, 'rgba(255,255,255,0.55)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.fillRect(0, 0, 128, 128);
    // Неровный край — клуб, а не шар.
    g.globalCompositeOperation = 'destination-out';
    for (let i = 0; i < 18; i++) {
      const a = rnd() * Math.PI * 2; const
        r = rand(40, 62);
      g.beginPath(); g.arc(64 + Math.cos(a) * r, 64 + Math.sin(a) * r, rand(8, 16), 0, Math.PI * 2); g.fill();
    }
    const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; return t;
  })();
  const smokeColor = new THREE.Color(0xf2ebe6);
  const WIND = new THREE.Vector3(1, 0, -0.35).normalize();
  const puffs = [];
  const PUFF_LIFE = 14; // с — сколько живёт клуб
  smokeSources.forEach((src) => {
    // Каждый клуб — отдельный спрайт и вызов отрисовки: клубов меньше, но
    // они крупнее, шлейф той же плотности.
    const count = 20;
    for (let i = 0; i < count; i++) {
      const m = new THREE.SpriteMaterial({
        map: smokeTex, color: smokeColor, transparent: true, depthWrite: false, fog: true,
      });
      const sp = new THREE.Sprite(m);
      sp.userData = {
        src, age: (i / count) * PUFF_LIFE, spin: rand(-0.3, 0.3), jitter: new THREE.Vector3(rand(-1, 1), rand(-1, 1), rand(-1, 1)),
      };
      m.rotation = rnd() * 6;
      scene.add(sp); puffs.push(sp);
    }
  });
  function updateSmoke(dt) {
    puffs.forEach((sp) => {
      const u = sp.userData; const
        { src } = u;
      u.age = (u.age + dt) % PUFF_LIFE;
      const k = u.age / PUFF_LIFE;
      // Подъём замедляется, снос ветром растёт.
      const rise = src.r * 5 * Math.sqrt(k); const
        drift = src.r * 14 * k;
      sp.position.set(src.x, src.y - 6, src.z)
        .addScaledVector(WIND, drift)
        .add(new THREE.Vector3(0, rise, 0))
        .addScaledVector(u.jitter, src.r * 1.2 * k);
      const size = src.r * (1.9 + k * 5.8);
      sp.scale.set(size, size, 1);
      sp.material.opacity = Math.min(1, u.age / 0.8) * (1 - k) * 0.75;
      sp.material.rotation += u.spin * dt;
      sp.material.color.copy(smokeColor);
    });
  }

  yield 0.53;
  // ─── Разрез «Богатырь»: уступы, экскаваторы, самосвалы ───────────────────
  {
    const p = ZONES.pit;
    // Уступчатая чаша: вертикальные стенки и полки, овал по размеру разреза.
    const profile = [];
    const ledge = 1 / (PIT_LEVELS * 1.15);
    for (let k = 0; k < PIT_LEVELS; k++) {
      const r = 1 - k * ledge;
      profile.push(
        new THREE.Vector2(r, -k * PIT_STEP),
        new THREE.Vector2(r, -(k + 1) * PIT_STEP),
        new THREE.Vector2(r - ledge, -(k + 1) * PIT_STEP),
      );
    }
    const bowl = new THREE.Mesh(
      new THREE.LatheGeometry(profile.reverse(), 64),
      lambert(0x3f352b, { side: THREE.DoubleSide }),
    );
    bowl.scale.set(p.rx, 1, p.rz); bowl.position.set(p.x, 0.2, p.z); scene.add(bowl);
    // Светлые дороги-серпантины на полках.
    for (let k = 1; k < PIT_LEVELS; k += 2) {
      const r = 1 - k * ledge - ledge * 0.5;
      const ring = new THREE.Mesh(new THREE.RingGeometry(r - ledge * 0.12, r + ledge * 0.12, 64), lambert(0x6a5a48, { side: THREE.DoubleSide }));
      ring.rotation.x = -Math.PI / 2; ring.scale.set(p.rx, p.rz, 1);
      ring.position.set(p.x, -(k + 1) * PIT_STEP + 0.35, p.z); scene.add(ring);
    }
    const yellow = lambert(0xe0a526); const
      dark = lambert(0x333333);
    for (let i = 0; i < 6; i++) {
      const a = rnd() * Math.PI * 2; const
        rr = rand(0.35, 0.8);
      const x = p.x + Math.cos(a) * p.rx * rr; const z = p.z + Math.sin(a) * p.rz * rr; const
        y = pitFloor(x, z);
      const ry = rnd() * 6;
      const g = new THREE.Group(); g.position.set(x, y, z); g.rotation.y = ry; scene.add(g);
      const body = new THREE.Mesh(box, yellow); body.scale.set(9, 6, 12); body.position.y = 5; g.add(body);
      const track = new THREE.Mesh(box, dark); track.scale.set(11, 2.4, 13); track.position.y = 1.2; g.add(track);
      const boom = new THREE.Mesh(box, yellow); boom.scale.set(1.4, 1.4, 22); boom.position.set(0, 12, -14); boom.rotation.x = 0.5; g.add(boom);
      const bucket = new THREE.Mesh(box, dark); bucket.scale.set(4, 3.5, 3.5); bucket.position.set(0, 6, -24); g.add(bucket);
      const lamp = new THREE.Mesh(box, lightMat(0x77705a, 0xfff0b0)); lamp.scale.set(1.2, 0.8, 0.2); lamp.position.set(0, 8, -6.1); g.add(lamp);
    }
    for (let i = 0; i < 10; i++) {
      const a = rnd() * Math.PI * 2; const
        rr = rand(0.25, 0.9);
      const x = p.x + Math.cos(a) * p.rx * rr; const
        z = p.z + Math.sin(a) * p.rz * rr;
      add(box, lambert(0xd9d4c4), x, pitFloor(x, z) + 2.4, z, 5, 4, 9, rnd() * 6);
    }
  }

  yield 0.60;
  // ─── ЛЭП: обычные опоры и гиганты 1150 кВ ────────────────────────────────
  // Опора как настоящая: широкое основание сужается к «талии», выше — узкая
  // мачта с тремя траверсами-треугольниками (средняя длиннее) и гирляндами
  // изоляторов. Все балки — инстансы одной коробки: сотни балок, один draw call.
  const pylonBeams = new Map(); // цвет → список балок
  const insulators = [];
  const Y_AXIS = new THREE.Vector3(0, 1, 0);
  function beam(list, a, b, thick) {
    const mid = a.clone().add(b).multiplyScalar(0.5); const dir = b.clone().sub(a); const
      len = dir.length();
    list.push({
      x: mid.x,
      y: mid.y,
      z: mid.z,
      sx: thick,
      sy: len,
      sz: thick,
      q: new THREE.Quaternion().setFromUnitVectors(Y_AXIS, dir.normalize()),
    });
  }
  // Где висят провода: высота (доля h) и вылет траверсы (доля h).
  const ARMS = [{ t: 0.62, l: 0.26 }, { t: 0.73, l: 0.34 }, { t: 0.84, l: 0.26 }];
  function addPylon(pos, yaw, h, color) {
    if (!pylonBeams.has(color)) pylonBeams.set(color, []);
    const list = pylonBeams.get(color);
    const m = new THREE.Matrix4().makeRotationY(yaw).setPosition(pos);
    const P = (x, y, z) => new THREE.Vector3(x, y, z).applyMatrix4(m);
    const th = 0.25 + h * 0.006;
    const waistY = h * 0.55; const
      topY = h * 0.95;
    const half = (y) => (y <= waistY ? THREE.MathUtils.lerp(h * 0.17, h * 0.045, y / waistY)
      : THREE.MathUtils.lerp(h * 0.045, h * 0.02, (y - waistY) / (topY - waistY)));
    const levels = [0, 0.12, 0.24, 0.35, 0.45, 0.55, 0.62, 0.68, 0.73, 0.79, 0.84, 0.9, 0.95].map((k) => k * h);
    const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]];
    const ring = (y) => corners.map(([cx, cz]) => P(cx * half(y), y, cz * half(y)));
    for (let i = 0; i < levels.length - 1; i++) {
      const lo = ring(levels[i]); const
        hi = ring(levels[i + 1]);
      for (let c = 0; c < 4; c++) {
        const n = (c + 1) % 4;
        beam(list, lo[c], hi[c], th * 1.6); // пояс (нога)
        beam(list, lo[c], hi[n], th * 0.7); // раскос
        beam(list, lo[n], hi[c], th * 0.7); // встречный раскос
        beam(list, hi[c], hi[n], th * 0.7); // горизонталь
      }
    }
    // Траверсы: верхняя и нижняя балка сходятся к концу, на конце изолятор.
    ARMS.forEach(({ t, l }) => {
      const y = h * t; const w = half(y); const
        L = h * l;
      [-1, 1].forEach((side) => {
        const tip = P(side * L, y, 0);
        [-1, 1].forEach((dz) => {
          beam(list, P(side * w, y, dz * w), tip, th * 0.8);
          beam(list, P(side * w, y + h * 0.05, dz * w * 0.6), tip, th * 0.6);
        });
        const ins = P(side * L, y - h * 0.035, 0);
        insulators.push({
          x: ins.x, y: ins.y, z: ins.z, sx: 1, sy: h * 0.07, sz: 1,
        });
      });
    });
    // Макушка для грозотроса.
    beam(list, P(0, topY, 0), P(0, h * 1.03, 0), th * 1.2);
  }
  const wireMat = new THREE.LineBasicMaterial({ color: 0x2a2a30, transparent: true, opacity: 0.8 });
  // Все провода — одна геометрия отрезков: один вызов отрисовки вместо линии
  // на каждый провод.
  const wirePoints = [];
  const pylonTops = [];
  function pylonLine(from, to, step, h, color = 0x5c6068) {
    const dir = new THREE.Vector3().subVectors(to, from);
    const n = Math.max(1, Math.floor(dir.length() / step));
    const yaw = Math.atan2(dir.x, dir.z) + Math.PI / 2; // траверсы поперёк линии
    const tops = [];
    for (let i = 0; i <= n; i++) {
      const p = from.clone().addScaledVector(dir, i / n);
      p.y = Math.max(0, height(p.x, p.z));
      addPylon(p, yaw, h, color);
      tops.push(p); pylonTops.push({ p, h, yaw });
    }
    // Провода висят с концов траверс (под изоляторами), провис посередине.
    const across = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));
    [-1, 1].forEach((side) => ARMS.forEach(({ t, l }) => {
      const pts = []; const
        off = across.clone().multiplyScalar(side * h * l);
      for (let i = 0; i < tops.length - 1; i++) {
        for (let s = 0; s <= 12; s++) {
          const u = s / 12; const
            p = tops[i].clone().lerp(tops[i + 1], u).add(off);
          p.y = THREE.MathUtils.lerp(tops[i].y, tops[i + 1].y, u) + h * (t - 0.07) - Math.sin(u * Math.PI) * h * 0.1;
          pts.push(p);
        }
      }
      for (let i = 0; i < pts.length - 1; i++) wirePoints.push(pts[i], pts[i + 1]);
    }));
  }
  const G = ZONES.gres;
  // 1150 кВ — самая высоковольтная ЛЭП в мире: опоры-гиганты от ГРЭС на восток.
  pylonLine(new THREE.Vector3(G.x + 160, 0, G.z + 10), new THREE.Vector3(700, 0, -330), 170, 80, 0x6a6e76);
  // Обычные линии: ГРЭС → ЦОД, ГРЭС → город.
  pylonLine(new THREE.Vector3(G.x + 120, 0, G.z + 60), new THREE.Vector3(200, 0, -135), 110, 42);
  pylonLine(new THREE.Vector3(G.x - 90, 0, G.z + 40), new THREE.Vector3(ZONES.city.x + 40, 0, ZONES.city.z - 160), 120, 40);

  scene.add(new THREE.LineSegments(new THREE.BufferGeometry().setFromPoints(wirePoints), wireMat));
  pylonBeams.forEach((list, color) => instanced(box, lambert(color), list));
  instanced(new THREE.CylinderGeometry(0.5, 0.5, 1, 6), lambert(0xcfd6dc), insulators);

  // Подстанция у кампуса.
  {
    const sub = [];
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 4; j++) {
        const x = 215 + i * 12; const
          z = -115 + j * 15;
        sub.push({
          x, y: 5, z: z - 4, sx: 0.4, sy: 10, sz: 0.4,
        }, {
          x, y: 5, z: z + 4, sx: 0.4, sy: 10, sz: 0.4,
        });
        sub.push({
          x, y: 10, z, sx: 0.4, sy: 0.4, sz: 8.4,
        }, {
          x: x + 5, y: 2, z, sx: 2.6, sy: 4, sz: 4,
        });
      }
    }
    instanced(box, lambert(0xa8adb4), sub);
  }

  instanced(box, lambert(0x6a6d73), lampPoles);
  instanced(box, lightMat(0x8a8474, 0xffd98a), lampHeads);

  yield 0.67;
  // ─── Город: панельные девятиэтажки, частный сектор, ДК, стела ───────────
  // По референсам: светлые панельки с полосами балконов, голубые крыши
  // частных домов, бежевый ДК с синей крышей и стеклом, газоны, живые
  // изгороди, берёзы, билборд, телевышка.
  const cityCells = [];
  {
    const c = ZONES.city;
    add(box, lambert(0x6f8f4e), c.x, 0.2, c.z, 430, 0.3, 330); // газоны
    for (let i = -2; i <= 2; i++) {
      road(c.x + i * 80, c.z - 160, c.x + i * 80, c.z + 160, 10, 25);
      road(c.x - 210, c.z + i * 64, c.x + 210, c.z + i * 64, 8, 0);
    }
    const panel = [lambert(0xe3e3de), lambert(0xd6d9dc), lambert(0xe9e2d4)];
    const band = lambert(0xa9adb3);
    const roofFlat = lambert(0x8d9096);
    const winMat = lightMat(0x5d6670, 0xffd58a);
    const winOff = lightMat(0x5d6670, 0x2a3040);
    const wOn = []; const wOff = []; const bands = []; const hedges = []; const trunks = []; const
      crowns = [];
    const blueRoof = lambert(0x2f6fd0); const
      houseWall = lambert(0xe8e0cf);

    // Панельный дом: длинная коробка, полосы балконов, окна.
    function panelBlock(x, z, len, floors, rotated) {
      const h = floors * 2.2; const
        d = 9;
      const sx = rotated ? d : len; const
        sz = rotated ? len : d;
      add(box, pick(panel), x, h / 2, z, sx, h, sz);
      add(box, roofFlat, x, h + 0.3, z, sx + 0.4, 0.6, sz + 0.4);
      for (let f = 1; f < floors; f++) {
        const y = f * 2.2;
        bands.push({
          x, y, z, sx: sx + 0.5, sy: 0.35, sz: sz + 0.5,
        });
        for (let u = -len / 2 + 1.5; u < len / 2 - 1; u += 2.4) {
          [d / 2 + 0.05, -d / 2 - 0.05].forEach((o) => {
            const it = rotated ? {
              x: x + o, y: y + 1.1, z: z + u, sx: 0.1, sy: 1.1, sz: 1.2,
            }
              : {
                x: x + u, y: y + 1.1, z: z + o, sx: 1.2, sy: 1.1, sz: 0.1,
              };
            (rnd() < 0.45 ? wOn : wOff).push(it);
          });
        }
      }
    }
    // Частный дом с голубой крышей.
    function house(x, z) {
      add(box, houseWall, x, 2, z, 8, 4, 7);
      const roof = add(new THREE.ConeGeometry(6.2, 3, 4), blueRoof, x, 5.5, z, 1, 1, 0.85, Math.PI / 4);
    }

    for (let i = -2; i < 2; i++) {
      for (let j = -2; j < 2; j++) {
        const cx = c.x + i * 80 + 40; const
          cz = c.z + j * 64 + 32;
        cityCells.push({ x: cx, z: cz });
        if (i === 0 && j === 0) continue; // место ДК
        if (i === -2) { // окраина — частный сектор
          for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b += 2) house(cx + a * 20, cz + b * 13);
          continue;
        }
        const floors = rnd() < 0.6 ? 9 : 5;
        if (rnd() < 0.5) { panelBlock(cx, cz - 14, 58, floors, false); panelBlock(cx, cz + 14, 58, floors === 9 ? 5 : 9, false); } else { panelBlock(cx - 18, cz, 44, floors, true); panelBlock(cx + 18, cz, 44, 9, true); }
        // Изгороди и берёзы во дворах и вдоль улиц.
        for (let u = -34; u <= 34; u += 3.4) {
          hedges.push({
            x: cx + u, y: 0.8, z: cz + 29, sx: 2.6, sy: 1.3, sz: 1.4,
          });
        }
        for (let k = 0; k < 5; k++) {
          const x = cx + rand(-32, 32); const z = cz + rand(-26, 26); const
            h = rand(7, 11);
          if (Math.abs(z - cz) < 20 && Math.abs(x - cx) < 30) continue;
          trunks.push({
            x, y: h * 0.35, z, sx: 0.45, sy: h * 0.7, sz: 0.45,
          });
          crowns.push({
            x, y: h * 0.8, z, sx: rand(2.4, 3.4), sy: h * 0.4, sz: rand(2.4, 3.4), ry: rnd() * 3,
          });
        }
      }
    }
    instanced(box, band, bands);
    instanced(box, winMat, wOn);
    instanced(box, winOff, wOff);
    instanced(box, lambert(0x6d7f3e), hedges);
    instanced(box, lambert(0xece6da), trunks);
    instanced(new THREE.IcosahedronGeometry(1, 0), lambert(0x8fae4e), crowns);

    // ДК: бежевая коробка, синяя «летящая» крыша, стеклянный фасад.
    {
      const x = c.x + 40; const
        z = c.z + 32;
      add(box, lambert(0xd8c9a3), x - 12, 9, z, 30, 18, 40);
      add(box, lambert(0xcfc3a0), x + 14, 7, z + 4, 26, 14, 30);
      const roof = add(box, lambert(0x2c55a8), x - 12, 19, z, 34, 1.2, 44); roof.rotation.z = 0.08;
      add(box, lambert(0x2c55a8), x + 14, 14.6, z + 4, 28, 1, 32);
      const glass = lightMat(0x2e5a9a, 0x7fb8ff); const
        panes = [];
      for (let k = -3; k <= 3; k++) {
        panes.push({
          x: x + 14 + k * 3.4, y: 6.5, z: z + 19.1, sx: 3, sy: 11, sz: 0.2,
        });
      }
      instanced(box, glass, panes);
    }
    // Телевышка: решётчатая мачта в красно-белую полоску с огнём наверху.
    {
      const x = c.x - 60; const
        z = c.z - 140;
      for (let k = 0; k < 10; k++) add(new THREE.CylinderGeometry(1.4 - k * 0.1, 1.5 - k * 0.1, 9, 4), lambert(k % 2 ? 0xf2f2f2 : 0xc8372d), x, 4.5 + k * 9, z);
      const l = add(new THREE.SphereGeometry(1.1, 8, 6), lightMat(0x8a2a22, 0xff3b30), x, 92, z); redLights.push(l);
    }
    // Билборд у дороги.
    {
      const x = c.x + 150; const
        z = c.z - 175;
      add(box, lambert(0x6a6d73), x, 4, z, 0.6, 8, 0.6);
      const bc = document.createElement('canvas'); bc.width = 256; bc.height = 96;
      const bx = bc.getContext('2d'); const gr = bx.createLinearGradient(0, 0, 256, 0);
      gr.addColorStop(0, '#6ec3ff'); gr.addColorStop(1, '#8ee06a'); bx.fillStyle = gr; bx.fillRect(0, 0, 256, 96);
      bx.fillStyle = '#fff'; bx.font = 'bold 26px -apple-system, sans-serif'; bx.fillText('GROOVE', 18, 44);
      bx.font = '16px -apple-system, sans-serif'; bx.fillText('Лети с нами!', 18, 72);
      const t = new THREE.CanvasTexture(bc); t.colorSpace = THREE.SRGBColorSpace;
      const face = lightMat(0xffffff, 0xffffff); face.map = t;
      add(new THREE.PlaneGeometry(16, 6), face, x, 10, z + 0.4);
    }
  }

  yield 0.73;
  // ─── Стела «ЕКІБАСТҰЗ» на въезде: солнце с лучами в чёрном кольце ────────
  {
    const x = -75; const z = 175; const
      ry = 0.9;
    const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = ry; scene.add(g);
    const stoneC = document.createElement('canvas'); stoneC.width = 512; stoneC.height = 96;
    const sc = stoneC.getContext('2d'); sc.fillStyle = '#7a5a36'; sc.fillRect(0, 0, 512, 96);
    for (let i = 0; i < 260; i++) { sc.fillStyle = pick(['#8d6a40', '#6b4d2e', '#a07a4a', '#5e4428']); sc.fillRect(rnd() * 512, rnd() * 96, 10 + rnd() * 24, 6 + rnd() * 12); }
    sc.fillStyle = '#ffd400'; sc.font = 'bold 64px -apple-system, sans-serif'; sc.textAlign = 'center'; sc.fillText('ЕКІБАСТҰЗ', 256, 72);
    const st = new THREE.CanvasTexture(stoneC); st.colorSpace = THREE.SRGBColorSpace;
    const stone = lambert(0x7a5a36); const
      front = new THREE.MeshLambertMaterial({ map: st });
    const base = new THREE.Mesh(box, [stone, stone, stone, stone, front, stone]);
    base.scale.set(26, 5, 5); base.position.y = 2.5; g.add(base);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(9, 0.9, 8, 40), lambert(0x1c1c1e));
    ring.position.set(-1, 16, 0); ring.rotation.y = 0.35; g.add(ring);
    const sunMat = lambert(0xf2c21b, { emissive: 0x5a4200 });
    const sunBall = new THREE.Mesh(new THREE.IcosahedronGeometry(2.6, 1), sunMat); sunBall.position.copy(ring.position); g.add(sunBall);
    for (let k = 0; k < 20; k++) {
      const a = k / 20 * Math.PI * 2; const
        len = k % 2 ? 7.5 : 10.5;
      const ray = new THREE.Mesh(new THREE.ConeGeometry(0.28, len, 4), sunMat);
      ray.position.set(ring.position.x + Math.cos(a) * (2.6 + len / 2), ring.position.y + Math.sin(a) * (2.6 + len / 2), 0);
      ray.rotation.z = a - Math.PI / 2; g.add(ray);
    }
    [[-4, -1], [2, 1]].forEach(([dx, dz]) => { const leg = new THREE.Mesh(box, lambert(0x1c1c1e)); leg.scale.set(0.8, 6, 0.8); leg.position.set(dx, 7, dz); g.add(leg); });
  }

  yield 0.80;
  // ─── Звёзды для сбора ────────────────────────────────────────────────────
  const starShape = new THREE.Shape();
  for (let i = 0; i < 10; i++) {
    const a = i / 10 * Math.PI * 2 - Math.PI / 2; const
      r = i % 2 ? 0.45 : 1;
    i ? starShape.lineTo(Math.cos(a) * r, Math.sin(a) * r) : starShape.moveTo(Math.cos(a) * r, Math.sin(a) * r);
  }
  const starGeo = new THREE.ExtrudeGeometry(starShape, { depth: 0.35, bevelEnabled: false }); starGeo.center();
  const starMat = new THREE.MeshLambertMaterial({ color: 0xffd23f, emissive: 0x8a5a00 });
  const pickups = [];
  function star(x, y, z) { const s = add(starGeo, starMat, x, y, z, 2.4, 2.4, 2.4); pickups.push(s); }
  halls.forEach((hl) => [-0.3, 0, 0.3].forEach((k) => star(hl.x + hl.w * k, hl.h + 10, hl.z)));
  cityCells.forEach((cc) => star(cc.x, 30, cc.z));
  // Спираль вокруг большой трубы.
  for (let i = 0; i < 16; i++) {
    const a = i * 0.8; star(G.x + 30 + Math.cos(a) * 45, 40 + i * 7, G.z - 80 + Math.sin(a) * 45);
  }
  // В карьер — нырять.
  const pitStarStep = (ZONES.pit.rx * 2) / 7;
  for (let i = 0; i < 8; i++) {
    star(ZONES.pit.x - ZONES.pit.rx + i * pitStarStep, 30 - Math.sin((i / 7) * Math.PI) * 90, ZONES.pit.z);
  }
  // Между опорами-гигантами.
  pylonTops.slice(0, 7).forEach(({ p, h }) => star(p.x, h * 0.55, p.z));

  yield 0.87;
  // ─── Время суток ─────────────────────────────────────────────────────────
  // Ключевые точки: день → закат → ночь.
  const K = [
    {
      t: 0.0, top: 0x3f7fc4, mid: 0x9cc7e8, horizon: 0xf1ddb8, fog: 0xd6ccb8, sun: 0xfff2dc, sunI: 1.7, hemi: 1.5, sunAlt: 0.6,
    },
    {
      t: 0.5, top: 0x2e3a6e, mid: 0xb0708c, horizon: 0xffb266, fog: 0xd9a07e, sun: 0xffa05a, sunI: 1.5, hemi: 1.1, sunAlt: 0.04,
    },
    {
      t: 1.0, top: 0x05081a, mid: 0x0f1838, horizon: 0x2a2748, fog: 0x161b33, sun: 0x8fa6ff, sunI: 0.55, hemi: 0.8, sunAlt: -0.25,
    },
  ];
  const tmpA = new THREE.Color(); const
    tmpB = new THREE.Color();
  function lerpHex(a, b, k) { return tmpA.setHex(a).lerp(tmpB.setHex(b), k).clone(); }
  function setTime(t) {
    const i = t < 0.5 ? 0 : 1; const a = K[i]; const b = K[i + 1]; const
      k = (t - a.t) / (b.t - a.t);
    skyUniforms.top.value.copy(lerpHex(a.top, b.top, k));
    skyUniforms.mid.value.copy(lerpHex(a.mid, b.mid, k));
    skyUniforms.horizon.value.copy(lerpHex(a.horizon, b.horizon, k));
    fog.color.copy(lerpHex(a.fog, b.fog, k));
    const sunColor = lerpHex(a.sun, b.sun, k);
    const alt = THREE.MathUtils.lerp(a.sunAlt, b.sunAlt, k);
    const sunDir = new THREE.Vector3(0.45, Math.max(alt, -0.2), -1).normalize();
    sun.position.copy(sunDir).multiplyScalar(1000);
    sun.color.copy(sunColor); sun.intensity = THREE.MathUtils.lerp(a.sunI, b.sunI, k);
    hemi.intensity = THREE.MathUtils.lerp(a.hemi, b.hemi, k);
    hemi.color.copy(lerpHex(0xffffff, 0x9fb0ff, smooth(0.5, 1, t)));
    skyUniforms.sunDir.value.copy(sunDir);
    skyUniforms.sunColor.value.copy(sunColor);
    skyUniforms.sunStrength.value = 1 - smooth(0.62, 0.8, t);
    // Огни зажигаются с закатом.
    const glow = smooth(0.35, 0.75, t);
    lights.forEach((l) => l.m.color.copy(l.day).lerp(l.night, glow));
    nightStars.material.opacity = smooth(0.65, 0.95, t);
    smokeColor.copy(lerpHex(0xf2ebe6, 0x5a5a6e, smooth(0.5, 1, t)));
    fog.near = THREE.MathUtils.lerp(FOG_NEAR, FOG_NEAR * 0.6, smooth(0.5, 1, t));
  }

  yield 0.93;
  // ─── Склейка статики ─────────────────────────────────────────────────────
  // Дороги, корпуса, трубы, дома, экскаваторы — сотни отдельных мешей по
  // 12 треугольников, каждый со своим вызовом отрисовки. Меши одного вида
  // (цвет, тип материала) в одном секторе сливаются в один. Огни (материалы
  // из lights) склеиваются только со своим же материалом — их цвет меняется
  // со временем суток. Мигающие огни, звёзды, небо, текстуры — как есть.
  {
    const keep = new Set([sky, ...pickups, ...redLights]);
    const lightMats = new Set(lights.map((l) => l.m));
    const matKey = (mat) => {
      if (lightMats.has(mat)) return mat.uuid;
      return [mat.type, mat.color?.getHex(), mat.emissive?.getHex(), mat.side, mat.flatShading,
        mat.transparent, mat.opacity, mat.vertexColors, mat.fog].join('|');
    };
    scene.updateMatrixWorld(true);
    const groups = new Map();
    const pos = new THREE.Vector3();
    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh) || obj instanceof THREE.InstancedMesh || keep.has(obj)) return;
      const mat = obj.material;
      const plain = mat instanceof THREE.MeshLambertMaterial || mat instanceof THREE.MeshBasicMaterial;
      if (Array.isArray(mat) || !plain || mat.map) return;
      obj.getWorldPosition(pos);
      const key = `${matKey(mat)}#${sectorOf(pos.x, pos.z)}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(obj);
    });
    groups.forEach((meshes) => {
      if (meshes.length < 2) return;
      const parts = meshes.map((mesh) => {
        const geo = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
        Object.keys(geo.attributes)
          .filter((name) => name !== 'position' && name !== 'normal')
          .forEach((name) => geo.deleteAttribute(name));
        geo.clearGroups();
        geo.applyMatrix4(mesh.matrixWorld);
        return geo;
      });
      const merged = mergeGeometries(parts, false);
      parts.forEach((g) => g.dispose());
      if (!merged) return;
      merged.computeBoundingSphere();
      meshes.forEach((mesh) => mesh.removeFromParent());
      scene.add(new THREE.Mesh(merged, meshes[0].material));
    });
  }

  setTime(0);
  return {
    height,
    pickups,
    spawn: {
      x: -10, y: 45, z: 300, yaw: 0,
    },
    radius: ISLAND * 0.92,
    maxY: 150,
    update(dt, elapsed, timeOfDay, cameraPosition) {
      setTime(timeOfDay);
      pickups.forEach((s) => { s.rotation.y = elapsed * 2; });
      updateSmoke(dt);
      redLights.forEach((l) => { l.visible = timeOfDay < 0.35 || Math.sin(elapsed * 3) > -0.3; });
      sky.position.copy(cameraPosition);
      nightStars.position.copy(cameraPosition);
    },
  };
}
